import { ErrorTypes } from '@fabric-ui/components';
import { envVariables } from '@fabric-ui/utils';
import { isDebugMode, isSynthetics } from '@utils/AvRum';
import { isUsableId, normalizeId } from '@utils/identity.utils';

declare const window: {
  pendo?: pendo.Pendo;
} & Window;

export const getPendo = () => window.pendo;

// Pendo is controlled by its own key only. Whether Sentry is configured has no effect on it.
const isPendoEnabled = () => !isDebugMode() && !isSynthetics && !!envVariables.VITE_PENDO_API_KEY;

const REQUIRED_PENDO_IDS = ['userId', 'accountId'] as const;

// Pendo keys all of its data on these IDs and cannot split records apart afterwards, so an event
// sent with a missing ID is lost for good. Refuse to initialise instead of sending `undefined_<env>`.
export const initializePendo = (pendoData: any) => {
  if (!isPendoEnabled()) return;
  if (!window.pendo) {
    console.error(`${ErrorTypes.Pendo}: Pendo is not initialized. The script might have failed to load.`);
    return;
  }
  if (!pendoData) {
    console.error(`${ErrorTypes.Pendo}: Pendo initialization data is missing.`);
    return;
  }
  const missingIds = REQUIRED_PENDO_IDS.filter(key => !isUsableId(pendoData[key]));
  if (missingIds.length) {
    console.error(`${ErrorTypes.Pendo}: Pendo initialization skipped, missing required ${missingIds.join(' and ')}.`);
    return;
  }
  const { email, role, roleId, roleName, accountName, accountType, accountCreated } = pendoData;
  const userId = normalizeId(pendoData.userId);
  const accountId = normalizeId(pendoData.accountId);
  try {
    window.pendo.initialize({
      visitor: {
        id: `${userId}_${envVariables.VITE_ENV}`,
        email: email?.replace(/^[^@]+/, '****') || '',
        role: role || '',
        roleId: roleId || '',
        roleName: roleName || '',
      },

      account: {
        id: `${accountId}_${envVariables.VITE_ENV}`,
        name: accountName || '',
        type: accountType || '',
        creationDate: accountCreated || '',
      },
    });
  } catch (error) {
    console.error(`${ErrorTypes.Pendo}: Error during Pendo initialization:`, error);
    console.error(`${ErrorTypes.Pendo}: Pendo initialization data:`, pendoData);
  }
};

// Ends the Pendo visitor/account session on logout so screens shown afterwards (e.g. login) are not attributed to the previous user.
// window.pendo only exists when the script was injected (not debug mode, not synthetic, key set), and its stub has no clearSession.
export const clearPendoSession = () => {
  window.pendo?.clearSession?.();
};

export const initializePendoScript = () => {
  if (isPendoEnabled()) {
    /* eslint-disable */
    (function (p, e, n, d) {
      let v;
      let w;
      let x;
      let y;
      let z;
      let o = (p[d] = p[d] || {});
      o._q = o._q || [];
      v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
      for (w = 0, x = v.length; w < x; ++w) {
        (function (method) {
          o[method] =
            o[method] ||
            function () {
              o._q[method === 'initialize' ? 'unshift' : 'push']([method].concat(Array.prototype.slice.call(arguments, 0)));
            };
        })(v[w]);
      }
      y = e.createElement(n);
      y.async = !0;
      y.src = `https://cdn.pendo.io/agent/static/${envVariables.VITE_PENDO_API_KEY}/pendo.js`;
      y.onerror = (...args) => {
        console.error(`${ErrorTypes.Pendo}: Pendo script failed to load.`, args);
      };
      y.onload = () => {
        if (p.pendo) {
          v.forEach(functionName => {
            const originalFn = p.pendo![functionName];
            if (typeof originalFn === 'function') {
              p.pendo![functionName] = function () {
                try {
                  return originalFn.apply(this, arguments);
                } catch (error) {
                  console.error(`${ErrorTypes.Pendo}: Error in pendo.${functionName}:`, error);
                  console.error(`${ErrorTypes.Pendo}: Arguments for pendo.${functionName}:`, Array.prototype.slice.call(arguments, 0));
                }
              };
            }
          });
        }
      };
      z = e.getElementsByTagName(n)[0];
      z.parentNode.insertBefore(y, z);
    })(window, document, 'script', 'pendo');
    /* eslint-enable */
  }
};
